import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { email, form, FormField, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  InvitePropertyMemberDto,
  PropertyDto,
  PropertyMemberDto,
  PropertyMemberRole,
  PropertyMemberStatus,
  UserDto,
} from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  AlertSnackBarService,
  ConfirmDialogAuraComponent,
} from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, filter, finalize, switchMap, tap } from 'rxjs';

export interface PropertyMembersDialogData {
  property: PropertyDto;
  isOwner?: boolean;
}

@Component({
  selector: 'etxe-property-members-dialog',
  imports: [
    CommonModule,
    FormField,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
  ],
  templateUrl: './property-members-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyMembersDialogComponent extends AbstractSignalFormComponent<InvitePropertyMemberDto> {
  private readonly dialogRef = inject(MatDialogRef<PropertyMembersDialogComponent>);
  private readonly data = inject<PropertyMembersDialogData>(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);
  private readonly api = inject(ApiService);
  private readonly alertSnackbar = inject(AlertSnackBarService);

  readonly PropertyMemberRole = PropertyMemberRole;
  readonly PropertyMemberStatus = PropertyMemberStatus;

  readonly property = signal<PropertyDto>(this.data.property);
  readonly members = computed<PropertyMemberDto[]>(() => this.property().members || []);
  readonly owner = computed<UserDto | undefined>(() => {
    const o = this.property().owner;
    return typeof o === 'object' && '_id' in o ? (o as UserDto) : undefined;
  });
  readonly isOwner = signal<boolean>(this.data.isOwner ?? true);

  readonly isInviting = signal<boolean>(false);
  readonly removingUserId = signal<string | null>(null);

  override model: WritableSignal<InvitePropertyMemberDto> = signal<InvitePropertyMemberDto>({
    email: '',
    role: PropertyMemberRole.CO_OWNER,
    firstName: '',
    lastName: '',
  });

  override formTree = form(this.model, (b) => {
    required(b.email);
    email(b.email);
    required(b.role);
  });

  readonly selectedRole = computed(() => this.model().role ?? PropertyMemberRole.CO_OWNER);

  getMemberUser(member: PropertyMemberDto): UserDto | null {
    if (member.user && typeof member.user === 'object') {
      return member.user as UserDto;
    }
    return null;
  }

  getMemberDisplayName(member: PropertyMemberDto): string {
    const user = this.getMemberUser(member);
    if (user?.identity?.firstName || user?.identity?.lastName) {
      return `${user.identity.firstName || ''} ${user.identity.lastName || ''}`.trim();
    }
    if (member.firstName || member.lastName) {
      return `${member.firstName || ''} ${member.lastName || ''}`.trim();
    }
    return user?.contactInfos?.email || member.email || 'Membre invité';
  }

  getMemberInitials(member: PropertyMemberDto): string {
    const user = this.getMemberUser(member);
    const first = user?.identity?.firstName?.[0] || member.firstName?.[0] || '';
    const last = user?.identity?.lastName?.[0] || member.lastName?.[0] || '';
    if (first || last) return `${first}${last}`.toUpperCase();
    return 'CO';
  }

  sendInvitation(): void {
    if (!this.isFormValid() || this.isInviting()) {
      return;
    }

    const val = this.model();
    const cleanEmail = val.email.trim().toLowerCase();

    const existing = this.members().find(
      (m) =>
        m.email?.toLowerCase() === cleanEmail ||
        this.getMemberUser(m)?.contactInfos?.email?.toLowerCase() === cleanEmail,
    );

    if (existing) {
      if (existing.status === PropertyMemberStatus.ACCEPTED) {
        this.alertSnackbar.error({ message: 'Cette personne est déjà membre de ce bien.' });
        return;
      }
      if (existing.status === PropertyMemberStatus.PENDING) {
        this.alertSnackbar.error({ message: 'Cette personne est déjà invitée sur ce bien.' });
        return;
      }
    }

    this.isInviting.set(true);

    const dto: InvitePropertyMemberDto = {
      email: cleanEmail,
      role: val.role,
      ...(val.firstName?.trim() ? { firstName: val.firstName.trim() } : {}),
      ...(val.lastName?.trim() ? { lastName: val.lastName.trim() } : {}),
    };

    this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .members(this.property()._id)
      .invite(dto)
      .pipe(
        tap((updatedProperty) => {
          this.property.set(updatedProperty);
          this.model.set({
            email: '',
            role: PropertyMemberRole.CO_OWNER,
            firstName: '',
            lastName: '',
          });
          this.alertSnackbar.success({ message: "L'invitation a été envoyée avec succès." });
          this.dialogRef.close(updatedProperty);
        }),
        catchError((err) => {
          this.alertSnackbar.error({
            message: err?.error?.message || "Une erreur est survenue lors de l'envoi de l'invitation.",
          });
          return EMPTY;
        }),
        finalize(() => this.isInviting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  removeMember(member: PropertyMemberDto): void {
    if (!this.isOwner()) {
      this.alertSnackbar.error({
        message: 'Seul le propriétaire principal peut retirer un membre du bien.',
      });
      return;
    }

    const user = this.getMemberUser(member);
    const identifier = user?._id ? String(user._id) : member.user ? String(member.user) : member.email;
    if (!identifier) return;

    const memberName = this.getMemberDisplayName(member);
    const isAccepted = member.status === PropertyMemberStatus.ACCEPTED;
    const title = isAccepted ? "Retirer l'accès à ce membre" : "Annuler l'invitation";
    const message = isAccepted
      ? `Êtes-vous sûr de vouloir retirer l'accès de ${memberName} à ce bien immobilier ?`
      : `Êtes-vous sûr de vouloir annuler l'invitation envoyée à ${memberName} ?`;
    const confirmText = isAccepted ? "Retirer l'accès" : "Annuler l'invitation";

    this.dialog
      .open(ConfirmDialogAuraComponent, {
        data: {
          title,
          message,
          confirm: {
            text: confirmText,
            class: 'primary destructive',
          },
          cancelText: 'Conserver',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => this.removingUserId.set(identifier)),
        switchMap(() =>
          this.api.business().etxe().properties().owner().members(this.property()._id).remove(identifier),
        ),
        tap((updatedProperty) => {
          this.property.set(updatedProperty);
          this.alertSnackbar.success({
            message: isAccepted ? 'Le membre a été retiré avec succès.' : "L'invitation a été annulée.",
          });
        }),
        catchError((err) => {
          this.alertSnackbar.error({
            message: err?.error?.message || 'Une erreur est survenue lors du retrait du membre.',
          });
          return EMPTY;
        }),
        finalize(() => this.removingUserId.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  close(): void {
    this.dialogRef.close(this.property());
  }
}
